import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useState } from 'react';

import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import { ImageRow } from '@/components/ItemCard/ImageRow';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    ItemCardSource,
    useTrackItemCardClicked,
} from '@/utils/amplitude/analytics/item/trackItemCardClicked.analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';

import { ItemCardComponent, ItemCardInfo } from '../../../../types/types';
import { ItemCardPriceRow } from '../../../components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '../../../components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '../../../components/ItemCardTitle/ItemCardTitle';

const UpcomingItemPageItemCard: ItemCardComponent = ({
    className,
    dimension,
    itemId,
    recommendationPosition,
    recommendationType,
}) => {
    const [hover, setHover] = useState(false);
    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition: recommendationPosition ?? 0,
        recommendationType,
    });
    const trackItemCardClicked = useTrackItemCardClicked();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));

    const handleMouseEnter = useCallback(() => setHover(true), []);
    const handleMouseLeave = useCallback(() => setHover(false), []);

    const handleClick = useCallback(
        (trigger: LotRecommendationTrigger) => {
            onRecommendationClick(trigger);
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.LotDetailsPage,
            });
        },
        [catalogId, itemId, onRecommendationClick, promoted, trackItemCardClicked]
    );

    return (
        <div
            className={cn(
                "relative grid [grid-template-rows:min-content_8px_20px_4px_min-content_4px_min-content] [grid-template-areas:'img'_'.'_'date'_'.'_'title'_'.'_'soldprice']",
                className
            )}
            data-testid="upcoming-items-page-item-card"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ gridTemplateColumns: dimension ? `${dimension}px` : '1fr' }}
        >
            <section className="relative">
                <span className="absolute top-xs right-xs z-[2]">
                    <ItemCardSaveIcon itemId={itemId} />
                </span>
                <div className="[grid-row:img]">
                    <ImageRow
                        dimension={dimension}
                        itemId={itemId}
                        onNavigate={() => handleClick(LotRecommendationTrigger.ImageClick)}
                    />
                </div>
            </section>
            <div className="[grid-row:date]">
                <ItemCardShortDate
                    itemId={itemId}
                    onMouseEnter={handleMouseLeave}
                    onMouseLeave={handleMouseEnter}
                />
            </div>
            <div className="[grid-row:title]">
                <ItemCardTitle
                    isHovered={hover}
                    itemId={itemId}
                    onNavigate={() => handleClick(LotRecommendationTrigger.TitleClick)}
                />
            </div>
            <div className="[grid-row:soldprice]">
                <ItemCardPriceRow
                    isHovered={hover}
                    itemId={itemId}
                />
            </div>
        </div>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 184, mobile: 157 },
    ItemCardComponent: UpcomingItemPageItemCard,
};

export default info;
